<!--
  The canvas. This is the only component that touches screen coordinates.

  The stage transform is *derived* from a `Viewport` owned by `lib/coords.ts`: the stage is
  given `scale({x: s, y: s})`, `rotation(theta)` and `position({x, y})`, which is exactly the
  transform `toStagePoint`/`toImagePoint` implement. Two consequences, both deliberate:

    * The boxes layer is drawn in **image pixel space** directly, so a dragged or resized node
      reports its geometry already in image pixels. No conversion, no chance of one being
      wrong in one direction only.
    * Pointer positions come from `stage.getPointerPosition()` (container pixels) and go
      through `toImagePoint`. Konva's own `getRelativePointerPosition` would be a second
      implementation of the same arithmetic, and a second implementation is how the two drift.

  Layer order is image -> boxes -> handles, and the image layer is `listening: false` so hit
  detection never walks the picture on an inscription with forty boxes.

  Rotation
  --------
  Most inscription photographs are not square-on, so the *view* turns and the annotator draws
  upright rectangles on a levelled glyph. What gets stored is the box's own angle on the
  unrotated photograph — the two are the same fact, and putting the rotation in the viewport is
  what lets tightness be judged against something level.

  The rotation lives on the stage, not on the boxes layer, so a box node's `x()`/`y()` are still
  image pixels and the property this whole component rests on survives. Three things do have to
  know about it: the drag builds its rectangle in the *view* frame (`orientedFromDrag`), each box
  node is positioned at its **centre** with a half-size offset so the Transformer turns it about
  its middle rather than a corner, and the labels are counter-rotated so text stays level however
  far the photograph is turned.
-->
<script lang="ts">
  import Konva from 'konva';
  import { untrack } from 'svelte';

  import { LABEL_OFFSET, boxLabelAttrs, boxRectAttrs } from '../lib/boxStyle';
  import {
    MIN_BOX_PIXELS,
    centerOf,
    cornersOf,
    fitViewport,
    isDrawable,
    orientedFromDrag,
    orientedFromNode,
    panViewport,
    rotateViewport,
    rotationToLevel,
    setRotationAbout,
    toImagePoint,
    zoomViewport,
    type OrientedRect,
    type Point,
    type Viewport,
  } from '../lib/coords';
  import type { DraftBox } from '../lib/draft';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { session } from '../lib/stores/session.svelte';

  interface Props {
    /** Bumped by the parent to request a re-fit (the "0" shortcut). */
    fitToken?: number;
    zoomToken?: number;
    zoomDirection?: number;
  }

  const { fitToken = 0, zoomToken = 0, zoomDirection = 0 }: Props = $props();

  let container = $state<HTMLDivElement | null>(null);
  /**
   * The stage transform. Reactive only so the zoom readout in the overlay updates — every
   * effect below that touches it does so inside `untrack`, because an effect that both reads
   * and writes `viewport` is an infinite update loop, and Svelte says so at runtime.
   */
  let viewport = $state<Viewport>({ scale: 1, x: 0, y: 0, rotation: 0 });
  let containerSize = { width: 1, height: 1 };
  let hint = $state<string | null>(null);

  /**
   * Has the annotator chosen this zoom/pan themselves?
   *
   * The `ResizeObserver` used to resize the Konva stage and re-apply the *old* viewport, so a
   * container that grew — collapsing the record rail, widening the window, folding the palette
   * away — left the photograph at its previous scale in the corner of a bigger canvas, with the
   * new space simply empty. `fit()` ran only from the Fit button, so the way to discover this
   * was to already know about it.
   *
   * Refitting unconditionally is the other bug: somebody zoomed to 400% on a weathered `ḏ` does
   * not want the picture yanked back to fit because a save banner appeared and changed the
   * column height. So the container follows the image only while the viewport is still the
   * one *this component* chose. Any deliberate zoom or pan hands control over; `fit()` — the
   * explicit "back to the whole stone" gesture, and the fresh-image case — hands it back.
   */
  let userAdjusted = false;

  /**
   * Why drawing is unavailable, or null if it is available.
   *
   * `session.canDraw` is a conjunction of three unrelated conditions and the UI used to
   * report none of them — pressing D flipped the label to "Draw mode" and dragging still
   * panned, with nothing on screen explaining the difference. Mirror the conjunction in
   * the same order so the message can never disagree with the capability.
   */
  const drawBlockedReason = $derived(
    session.canDraw
      ? null
      : // Order matters: a missing annotator id ALSO forces `readOnly` (see
        // EditorSession.#claim — a lock cannot be claimed without an id), so testing
        // `readOnly` first would blame a non-existent other annotator for what is
        // really an unset identity. Report the cause, not the symptom.
        !annotator.ready
        ? 'Set your annotator id before drawing. Every box records who drew it, so the tool will not accept one without an id — and it cannot claim the image lock either, which is why the image is read-only.'
        : session.lockLoss
          ? `Read-only — ${session.lockLoss.holder} holds this image's lock.`
          : session.readOnly
            ? 'Read-only — this image could not be claimed. See the save banner for the reason.'
            : session.imageUrl === null
              ? 'No image loaded yet — drawing is unavailable until the photograph is on screen.'
              : 'Drawing is unavailable.',
  );

  const modeLabel = $derived(
    !session.drawMode
      ? 'Pan mode — drag to pan'
      : session.canDraw
        ? 'Draw mode — drag to box, Shift+drag to pan'
        : 'Draw mode requested, but drawing is blocked — drag still pans',
  );

  let stage: Konva.Stage | null = null;
  let imageLayer: Konva.Layer | null = null;
  let boxLayer: Konva.Layer | null = null;
  let handleLayer: Konva.Layer | null = null;
  let transformer: Konva.Transformer | null = null;
  let previewRect: Konva.Rect | null = null;
  let konvaImage: Konva.Image | null = null;
  let observer: ResizeObserver | null = null;

  /** **Stage** pixels, not image pixels: the drag is axis-aligned in the view, not the image. */
  let dragStart: Point | null = null;
  let panStart: { pointer: Point; viewport: Viewport } | null = null;
  let panning = $state(false);
  /**
   * A Ctrl/Cmd-drag turning the view. Holds the viewport **as it was when the drag started**,
   * so each move computes an absolute rotation from the total horizontal travel rather than
   * accumulating deltas — the same reason `panStart` keeps its own copy. Accumulating would
   * drift, and drift in a rotation is drift in every box drawn afterwards.
   */
  let rotateStart: { pointer: Point; viewport: Viewport } | null = null;
  let rotating = $state(false);
  /** "Straighten" is armed and waiting for its two baseline clicks. */
  let straightening = $state(false);
  let straightenFrom: Point | null = null;
  /**
   * Which gesture modifier is being **held**, before any button goes down.
   *
   * Both of these gestures were silent until the press — which is the moment it is too late to
   * be told what the press does. Read from two sources because neither is sufficient on its
   * own: a key pressed while the pointer is still never reaches Konva, and a modifier already
   * down when the window took focus was never keyed down here. `blur` clears it, because a key
   * released over another window reports its `keyup` to that window and not to this one, and a
   * cursor left promising a gesture nobody is holding is worse than no cursor at all.
   */
  let held = $state<'rotate' | 'pan' | null>(null);

  function readModifiers(event: Pick<MouseEvent, 'ctrlKey' | 'metaKey' | 'shiftKey'>): void {
    // Same precedence as `onPointerDown`, and load-bearing for the same reason: Ctrl/Cmd is
    // tested first there so it turns the view even in draw mode with Shift also down. A cursor
    // that disagreed with the handler about which modifier wins would be a confident lie.
    held = event.ctrlKey || event.metaKey ? 'rotate' : event.shiftKey ? 'pan' : null;
  }

  /**
   * What pressing the button would do right now. Drives the cursor and nothing else.
   *
   * One `data-gesture` attribute rather than a class per state, so the five values are mutually
   * exclusive by construction. With a class each they are not: the old rule needed a compound
   * `.stage.rotating.drawing` selector precisely because two of them were true at once, and
   * adding the two "offered" states to that scheme is three more overlaps to spell out.
   */
  const gesture = $derived(
    rotating
      ? 'rotating'
      : panning
        ? 'panning'
        : held === 'rotate'
          ? 'rotate'
          : session.drawMode && session.canDraw && held !== 'pan'
            ? 'draw'
            : 'pan',
  );

  /**
   * One-shot suppression of Konva's synthetic `click`, armed by the pointer sequence that drew
   * a box. **Do not delete this without re-reading `Stage.js::_pointerup`.**
   *
   * Konva fires `mouseup` on the shape under the pointer first — that bubbles to the stage,
   * reaches `onPointerUp`, and creates the box — and *then*, in the same handler, fires `click`
   * on that shape if it is also where the pointer went down (`clickStartShape === shape`,
   * konva 10.3.0 `Stage.js:517-524`). `Konva._mouseListenClick` is the flag that would stop it,
   * and it is only cleared by a **Konva node drag** (`DragAndDrop.js:72-77`); the draw drag here
   * is not one, so the click fires.
   *
   * Since `canStartDrawOn` deliberately allows a drag to begin on top of a non-selected box —
   * that is the dense/overlapping-letter case the whole affordance exists for — a box drawn
   * starting and ending inside an existing box therefore ended with the *underlying* box
   * selected. `Del` then deleted the wrong box, `U`/`M`/`R` flagged the wrong box, and
   * `session.select` moved the cursor back onto the old box's token, so the next box was
   * auto-assigned an already-claimed chip.
   *
   * Konva offers no `preventDefault` for the synthetic click, so it is suppressed here instead.
   * Assigning `Konva._mouseListenClick = false` would also work and is what Konva does for its
   * own drags, but it is a private field of a third-party global; a local flag is the same
   * effect without a version-coupled write, and it is what `ImageCanvas.dom.test.ts` drives.
   */
  let suppressNextBoxClick = false;
  /**
   * Live nodes by box key. Kept so `redrawBoxes` can update attributes in place: it used to
   * `destroyChildren()` and rebuild 2n nodes on every box *and* selection change, which
   * includes the change a node's own `dragend`/`transformend` handler causes — destroying the
   * node whose handler is still running.
   */
  const nodes = new Map<string, { rect: Konva.Rect; label: Konva.Text }>();
  // Seeded from the incoming props so mounting does not count as a "fit" or "zoom" request —
  // before this was seeded, the canvas zoomed itself to 125% the moment it appeared. The
  // initial value is exactly what is wanted here, hence `untrack`.
  let lastFitToken = untrack(() => fitToken);
  let lastZoomToken = untrack(() => zoomToken);

  // Box appearance lives in `lib/boxStyle.ts` — the same module the `/styles` gallery renders
  // from, so nobody can look at the gallery and be shown something the canvas does not draw.

  // --- stage lifecycle -------------------------------------------------------------

  function ensureStage(node: HTMLDivElement): void {
    if (stage) return;
    stage = new Konva.Stage({ container: node, width: node.clientWidth, height: node.clientHeight });
    imageLayer = new Konva.Layer({ listening: false });
    boxLayer = new Konva.Layer();
    handleLayer = new Konva.Layer();
    stage.add(imageLayer, boxLayer, handleLayer);

    transformer = new Konva.Transformer({
      // On, as of oriented boxes. The handle turns the box about its own centre, which is
      // what the half-size offset in `styleNode` arranges; without that offset Konva would
      // turn it about its top-left corner and the glyph would swing out from under it.
      rotateEnabled: true,
      // No snaps. A baseline's tilt is whatever the photographer's angle made it, and
      // snapping to 15-degree stops would round every box away from the glyph it is on.
      rotationSnaps: [],
      keepRatio: false,
      ignoreStroke: true,
      // Konva defaults this to true. A handle dragged past the opposite edge then gives the
      // node a NEGATIVE scale, so the size read back off it would be negative — a box nothing
      // can render and the API rejects. `orientedFromNode` handles a flip correctly
      // regardless; this stops one from being producible in the first place.
      flipEnabled: false,
      enabledAnchors: [
        'top-left',
        'top-center',
        'top-right',
        'middle-left',
        'middle-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ],
      borderStroke: '#6ea8ff',
      anchorStroke: '#6ea8ff',
      anchorFill: '#12151a',
      rotateAnchorOffset: 24,
    });
    handleLayer.add(transformer);

    previewRect = new Konva.Rect({
      stroke: '#6ea8ff',
      strokeScaleEnabled: false,
      strokeWidth: 1.5,
      dash: [6, 4],
      visible: false,
      listening: false,
    });
    handleLayer.add(previewRect);

    stage.on('wheel', onWheel);
    stage.on('mousedown touchstart', onPointerDown);
    stage.on('mousemove touchmove', onPointerMove);
    stage.on('mouseup touchend', onPointerUp);
  }

  function applyViewport(): void {
    if (!stage) return;
    stage.scale({ x: viewport.scale, y: viewport.scale });
    stage.rotation(viewport.rotation);
    stage.position({ x: viewport.x, y: viewport.y });
    stage.batchDraw();
  }

  function fit(): void {
    const size = session.naturalSize;
    if (!size) return;
    // The rotation is kept across a re-fit. Pressing "fit" after straightening a photograph
    // in order to un-straighten it would be a surprising thing for a zoom control to do.
    viewport = fitViewport(size, containerSize, 24, viewport.rotation);
    userAdjusted = false;
    applyViewport();
    redrawBoxes();
  }

  /**
   * How far the view turns per pixel of horizontal drag.
   *
   * 0.25 puts a 120 px sweep at 30 degrees, which covers most baselines in this corpus in a
   * gesture that fits on a trackpad without lifting. It is deliberately coarse: this is the
   * "get it roughly there" control, and `,`/`.` or the buttons do the last degree. Straighten
   * (`T`) beats both when you can see the baseline.
   */
  const ROTATE_DEGREES_PER_PIXEL = 0.25;

  /** The container's middle, in stage pixels — the anchor a keyboard rotation turns about. */
  function containerCentre(): Point {
    return { x: containerSize.width / 2, y: containerSize.height / 2 };
  }

  /** Turn the view by `delta` degrees about the cursor, or the middle if it is outside. */
  export function rotateBy(delta: number): void {
    viewport = rotateViewport(viewport, pointer() ?? containerCentre(), delta);
    userAdjusted = true;
    applyViewport();
    redrawBoxes();
  }

  /**
   * Square the selected box up with the photograph **as it is currently being viewed**.
   *
   * Not "set it to zero". On a straightened view, level-on-screen is what the annotator means
   * by square, and that is `-viewport.rotation` on the photograph. Resetting to zero here
   * would un-straighten one box against every other box on the stone.
   */
  export function squareSelectedBox(): void {
    const key = session.selectedKey;
    if (!key) return;
    session.setBoxAngle(key, -viewport.rotation);
  }

  /** Put the photograph back the way the camera saw it. */
  export function resetRotation(): void {
    viewport = setRotationAbout(viewport, containerCentre(), 0);
    userAdjusted = true;
    applyViewport();
    redrawBoxes();
  }

  /**
   * Arm "straighten": the next two clicks name a baseline and the view levels it.
   *
   * Two clicks rather than a degrees field, because an annotator can point at a baseline and
   * cannot estimate its bearing — and because the number that field wants is the negative of
   * the one they would be tempted to type.
   */
  export function armStraighten(): void {
    straightenFrom = null;
    straightening = true;
    hint = 'Straighten: click two points along the baseline. Esc to cancel.';
  }

  export function cancelStraighten(): void {
    if (!straightening) return;
    straightening = false;
    straightenFrom = null;
    hint = null;
  }

  // --- pointer handling ------------------------------------------------------------

  function pointer(): Point | null {
    const position = stage?.getPointerPosition();
    return position ? { x: position.x, y: position.y } : null;
  }

  /** The single conversion. Container pixels in, image pixels out. */
  function pointerImage(): Point | null {
    const p = pointer();
    return p ? toImagePoint(viewport, p) : null;
  }

  function onWheel(event: Konva.KonvaEventObject<WheelEvent>): void {
    event.evt.preventDefault();
    const anchor = pointer();
    if (!anchor) return;
    const factor = event.evt.deltaY < 0 ? 1.12 : 1 / 1.12;
    viewport = zoomViewport(viewport, anchor, factor);
    userAdjusted = true;
    applyViewport();
    redrawBoxes();
  }

  /**
   * Can a drag starting on this target begin a new box?
   *
   * Yes on the stage, and yes on any box that is not the currently selected (draggable) one.
   * Requiring the stage meant that on a dense inscription — overlapping letters, ligatures —
   * a box could not be started inside an existing one at all, which is exactly where the
   * letters are. Only the selected box is draggable (see `redrawBoxes`), so "drag a box to
   * move it" still works and costs one extra click to select.
   */
  function canStartDrawOn(target: Konva.Node): boolean {
    if (target === stage) return true;
    if (target === previewRect) return false;
    if (target.getParent()?.className === 'Transformer') return false;
    return target instanceof Konva.Rect && !target.draggable();
  }

  /**
   * The two clicks of a "straighten" gesture, if one is armed. Returns true when the click was
   * consumed and normal drawing/panning must not also happen.
   */
  function handleStraightenClick(): boolean {
    if (!straightening) return false;
    const point = pointerImage();
    if (!point) return true;
    if (!straightenFrom) {
      straightenFrom = point;
      hint = 'Straighten: now click a second point further along the same baseline.';
      return true;
    }
    const degrees = rotationToLevel(straightenFrom, point);
    straightening = false;
    straightenFrom = null;
    viewport = setRotationAbout(viewport, pointer() ?? containerCentre(), degrees);
    userAdjusted = true;
    applyViewport();
    redrawBoxes();
    hint = `Straightened to ${degrees.toFixed(1)}°.`;
    return true;
  }

  function onPointerDown(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    // A new pointer sequence starts clean. Konva only fires the synthetic click when the
    // pointer went down and up on the same shape, so an armed flag can otherwise survive
    // unconsumed and swallow the *next* genuine click on a box.
    suppressNextBoxClick = false;
    readModifiers(event.evt);
    if (handleStraightenClick()) return;
    const start = pointer();
    if (!start) return;
    const onHandle = event.target.getParent()?.className === 'Transformer';
    if (onHandle) return;

    // A drag that was meant to draw but cannot, because nobody has said who they are. Ask,
    // rather than silently panning: this is the exact gesture the operator was making when
    // "pressed D, drawing did nothing" was reported, and a message in the overlay is only
    // read by someone who already suspects something is wrong.
    if (session.drawMode && !annotator.ready && !event.evt.shiftKey) {
      annotator.requestIdentity('draw');
    }

    // Ctrl/Cmd-drag turns the view, and is checked before drawing and panning so it works in
    // draw mode without toggling out of it. Cmd as well as Ctrl because `keymap.ts` already
    // treats the two as one for every keyboard shortcut, and because Ctrl-click is a
    // right-click on macOS — where Cmd is the gesture that actually survives.
    if (event.evt.ctrlKey || event.evt.metaKey) {
      rotateStart = { pointer: start, viewport: { ...viewport } };
      rotating = true;
      return;
    }

    const drawing = session.drawMode && session.canDraw && !event.evt.shiftKey;
    if (drawing && canStartDrawOn(event.target)) {
      // Kept in **stage** pixels, not image pixels: the rectangle the annotator is dragging is
      // axis-aligned in the view, and `orientedFromDrag` needs both raw endpoints to say so.
      dragStart = start;
      previewRect?.setAttrs({ width: 0, height: 0, visible: true });
      handleLayer?.batchDraw();
      return;
    }
    if (!drawing || event.evt.shiftKey) {
      panStart = { pointer: start, viewport: { ...viewport } };
      panning = true;
    }
  }

  function onPointerMove(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    readModifiers(event.evt);
    if (dragStart) {
      const now = pointer();
      if (!now) return;
      const rect = orientedFromDrag(viewport, dragStart, now);
      // The preview is a box node like any other, so it is positioned and turned like one —
      // otherwise the outline the annotator drags would not be the box they end up with.
      const centre = centerOf(rect);
      previewRect?.setAttrs({
        x: centre.x,
        y: centre.y,
        offsetX: rect.w / 2,
        offsetY: rect.h / 2,
        width: rect.w,
        height: rect.h,
        rotation: rect.angle,
      });
      handleLayer?.batchDraw();
      return;
    }
    if (rotateStart) {
      const now = pointer();
      if (!now) return;
      // Rightwards is clockwise, which is the same sense as a positive angle in image
      // coordinates — so the gesture and the readout agree without anyone having to remember
      // a sign. Vertical travel is ignored: one axis, one meaning.
      const degrees =
        rotateStart.viewport.rotation + (now.x - rotateStart.pointer.x) * ROTATE_DEGREES_PER_PIXEL;
      // Anchored on where the drag *started*, against the viewport as it was then. Anchoring
      // on the moving cursor would turn about a point that is itself sliding, and the glyph
      // under it would wander off screen.
      viewport = setRotationAbout(rotateStart.viewport, rotateStart.pointer, degrees);
      userAdjusted = true;
      applyViewport();
      redrawBoxes();
      return;
    }
    if (panStart) {
      const now = pointer();
      if (!now) return;
      // `panViewport`, not open-coded arithmetic: this component's contract is that it copies
      // numbers from `coords.ts` onto the stage and never computes its own.
      viewport = panViewport(
        panStart.viewport,
        now.x - panStart.pointer.x,
        now.y - panStart.pointer.y,
      );
      userAdjusted = true;
      applyViewport();
    }
  }

  function onPointerUp(): void {
    panStart = null;
    panning = false;
    if (rotateStart) {
      rotateStart = null;
      rotating = false;
      // No box is drawn by this gesture, and the click Konva is about to synthesise would
      // otherwise land on whatever shape the drag began on and move the selection.
      suppressNextBoxClick = true;
      return;
    }
    if (!dragStart) return;
    const end = pointer();
    previewRect?.visible(false);
    handleLayer?.batchDraw();
    const start = dragStart;
    dragStart = null;
    if (!end) return;

    const rect: OrientedRect = orientedFromDrag(viewport, start, end);
    if (!isDrawable(rect)) {
      // A drag that never moved is a click — usually a click that selected a box, now that a
      // drag may start on top of one. Saying "that was too small" every time an annotator
      // selects a box would train them to ignore the hint line.
      const wasAClick = rect.w < MIN_BOX_PIXELS / 4 && rect.h < MIN_BOX_PIXELS / 4;
      hint = wasAClick ? null : 'That drag was too small to be a letter. Nothing was recorded.';
      return;
    }
    hint = null;
    // `addBox` selects the box it created. Konva is about to fire `click` on whatever shape
    // this sequence started on — which, on a dense inscription, is an existing box — and that
    // handler would hand the selection (and the cursor) straight back to it.
    if (session.addBox(rect)) suppressNextBoxClick = true;
  }

  export function cancelDrag(): void {
    dragStart = null;
    panStart = null;
    panning = false;
    rotateStart = null;
    rotating = false;
    previewRect?.visible(false);
    handleLayer?.batchDraw();
  }

  // --- box rendering ---------------------------------------------------------------

  /**
   * Konva node -> store. **The one conversion the dataset's correctness hangs on.**
   *
   * `orientedFromNode` does the arithmetic, deriving the centre through the node's offset
   * rather than trusting `x()`/`y()` to be a corner; it is tested in `coords.test.ts`,
   * including the flipped-resize case and the offset case. Here we only read the node and hand
   * the numbers over — `redrawBoxes` then puts the node back in step with what was stored,
   * which is what folds away the leftover scale.
   */
  function commitGeometry(key: string, node: Konva.Rect): void {
    const rect = orientedFromNode({
      x: node.x(),
      y: node.y(),
      width: node.width(),
      height: node.height(),
      scaleX: node.scaleX(),
      scaleY: node.scaleY(),
      rotation: node.rotation(),
      offsetX: node.offsetX(),
      offsetY: node.offsetY(),
    });
    if (!rect) {
      // Non-finite. There is no honest rectangle to store, so leave the stored box alone and
      // say so rather than inventing one.
      hint = 'That box came back with an unreadable size. The change was discarded.';
      redrawBoxes();
      return;
    }
    // A box resized on a rotatable canvas is a considered upright box, not a legacy one.
    session.updateBoxGeometry(key, { ...rect, kind: 'obb' });
    redrawBoxes();
  }

  function styleNode(entry: { rect: Konva.Rect; label: Konva.Text }, box: DraftBox, unit: number): void {
    // Positioned at its CENTRE with a half-size offset, so Konva turns it about its middle.
    // Anchoring at the top-left instead would swing the box out from under the glyph the
    // moment the rotate handle moved. `coords.ts` reads the offset back rather than assuming
    // it, which is what keeps this choice local to the rendering.
    const centre = centerOf(box.bbox);
    entry.rect.setAttrs({
      x: centre.x,
      y: centre.y,
      offsetX: box.bbox.w / 2,
      offsetY: box.bbox.h / 2,
      width: box.bbox.w,
      height: box.bbox.h,
      // A resize leaves a scale on the node; the geometry it produced has already been folded
      // into w/h by `commitGeometry`, so asserting 1 here is what puts the node back in step.
      scaleX: 1,
      scaleY: 1,
      rotation: box.bbox.angle,
      ...boxRectAttrs(box),
      // Only the selected box is draggable, so a drag starting anywhere else can begin a new
      // box — see `canStartDrawOn`.
      draggable: !session.readOnly && box.key === session.selectedKey,
    });

    // The label sits above the box's own top-left corner, and is counter-rotated by the box's
    // angle *and* the view's so the text stays level on screen. Text that turned with the
    // photograph would be upside-down on any inscription read right-to-left.
    const corner = cornersOf(box.bbox)[0]!;
    const lift = rotatePointDegrees({ x: 0, y: -LABEL_OFFSET * unit }, -viewport.rotation);
    entry.label.setAttrs({
      x: corner.x + lift.x,
      y: corner.y + lift.y,
      rotation: -viewport.rotation,
      ...boxLabelAttrs(box, unit),
    });
  }

  /** Local helper: `coords.ts` keeps its own rotation private, and this is presentation. */
  function rotatePointDegrees(p: Point, degrees: number): Point {
    const radians = (degrees * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    return { x: p.x * cos - p.y * sin, y: p.x * sin + p.y * cos };
  }

  function redrawBoxes(): void {
    if (!boxLayer || !stage) return;
    const unit = 1 / viewport.scale;
    const seen = new Set<string>();
    let selectedNode: Konva.Rect | null = null;

    for (const box of session.boxes) {
      seen.add(box.key);
      let entry = nodes.get(box.key);
      if (!entry) {
        const key = box.key;
        const rect = new Konva.Rect({ name: key });
        const label = new Konva.Text({ listening: false });
        // Handlers close over the stable `key`, never over the box snapshot: the node outlives
        // any single version of the box now that nodes are reused.
        rect.on('click tap', () => {
          // Konva's synthetic click, tailing the drag that just drew a box on top of this one.
          // See `suppressNextBoxClick`.
          if (suppressNextBoxClick) {
            suppressNextBoxClick = false;
            return;
          }
          session.select(key);
        });
        rect.on('dragend', () => commitGeometry(key, rect));
        rect.on('transformend', () => commitGeometry(key, rect));
        boxLayer.add(rect, label);
        entry = { rect, label };
        nodes.set(key, entry);
      }
      styleNode(entry, box, unit);
      if (box.key === session.selectedKey) selectedNode = entry.rect;
    }

    for (const [key, entry] of [...nodes]) {
      if (seen.has(key)) continue;
      entry.rect.destroy();
      entry.label.destroy();
      nodes.delete(key);
    }

    transformer?.nodes(selectedNode ? [selectedNode] : []);
    boxLayer.batchDraw();
    handleLayer?.batchDraw();
  }

  // --- effects ---------------------------------------------------------------------
  //
  // Every effect below follows the same shape: read its trigger *tracked*, then do all the
  // imperative work inside `untrack`. The work touches `viewport`, which the same effect
  // writes; tracked, that is `effect_update_depth_exceeded` — and the failure mode is not a
  // visible crash but a workspace that silently stops loading, which is how it was found.

  $effect(() => {
    const node = container;
    if (!node) return;
    untrack(() => {
      ensureStage(node);
      const measure = () => {
        containerSize = { width: node.clientWidth, height: node.clientHeight };
        stage?.size({ width: containerSize.width, height: containerSize.height });
        // Follow the container while the viewport is still ours; leave a deliberate zoom or
        // pan alone. `fit()` no-ops until the image has loaded and reported its natural size,
        // so the mount-time call costs nothing. See `userAdjusted`.
        if (userAdjusted) applyViewport();
        else fit();
      };
      observer = new ResizeObserver(measure);
      observer.observe(node);
      measure();
    });
    return () => {
      observer?.disconnect();
      observer = null;
      stage?.destroy();
      stage = null;
      imageLayer = boxLayer = handleLayer = null;
      transformer = null;
      previewRect = null;
      konvaImage = null;
      // The stage owns the nodes; the map must not outlive them or a remount would style a
      // destroyed node.
      nodes.clear();
    };
  });

  // Load the picture. The presigned URL is used once, here, and never logged or stored.
  $effect(() => {
    const url = session.imageUrl;
    if (!url) return;
    let cancelled = false;
    untrack(() => {
      const element = new Image();
      element.crossOrigin = 'anonymous';
      element.onload = () => {
        if (cancelled || !imageLayer) return;
        untrack(() => {
          session.setNaturalSize({ width: element.naturalWidth, height: element.naturalHeight });
          konvaImage?.destroy();
          konvaImage = new Konva.Image({ image: element, x: 0, y: 0, listening: false });
          imageLayer?.add(konvaImage);
          fit();
          imageLayer?.batchDraw();
        });
      };
      element.onerror = () => {
        if (!cancelled) {
          hint =
            'The image could not be loaded. The presigned URL may have expired — reopen the image.';
        }
      };
      element.src = url;
    });
    return () => {
      cancelled = true;
    };
  });

  // Redraw when the box set, the selection, or the read-only state changes.
  $effect(() => {
    void session.boxes;
    void session.selectedKey;
    void session.readOnly;
    untrack(redrawBoxes);
  });

  $effect(() => {
    const token = fitToken;
    untrack(() => {
      if (token === lastFitToken) return;
      lastFitToken = token;
      fit();
    });
  });

  // The held-modifier cursor. Window-level rather than on the container, because the pointer
  // may be perfectly still while the key goes down and Konva only hears about a modifier when
  // the mouse also moves. Not `untrack`ed like the effects above: it touches no viewport state.
  $effect(() => {
    const onKey = (event: KeyboardEvent) => readModifiers(event);
    const clear = () => (held = null);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', clear);
    };
  });

  $effect(() => {
    const token = zoomToken;
    const direction = zoomDirection;
    untrack(() => {
      if (token === lastZoomToken) return;
      lastZoomToken = token;
      const anchor = { x: containerSize.width / 2, y: containerSize.height / 2 };
      viewport = zoomViewport(viewport, anchor, direction > 0 ? 1.25 : 1 / 1.25);
      userAdjusted = true;
      applyViewport();
      redrawBoxes();
    });
  });
</script>

<div class="canvas-wrap">
  <div class="stage" data-gesture={gesture} bind:this={container}></div>

  <div class="overlay-top small">
    <span class="mono">{(viewport.scale * 100).toFixed(0)}%</span>
    {#if session.naturalSize}
      <span class="muted mono">
        {session.naturalSize.width}×{session.naturalSize.height} px
      </span>
    {/if}
    <span class="muted">
      {modeLabel}
    </span>
  </div>

  <!--
    The rotation control. A mouse-only path is a failed design here, and so is a keyboard-only
    one: nobody discovers `,` `.` `;` `T` without being told. The readout is always visible so
    that a turned view can never be mistaken for a crooked photograph — which is the one way
    this feature could quietly mislead somebody about what they are looking at.
  -->
  <div class="overlay-rotate small">
    <button
      type="button"
      title="Turn anticlockwise (,). Ctrl/Cmd-drag on the photograph turns it freely."
      onclick={() => rotateBy(-1)}>↺</button
    >
    <span class="mono" class:turned={viewport.rotation !== 0}>
      {viewport.rotation.toFixed(1)}°
    </span>
    <button
      type="button"
      title="Turn clockwise (.). Ctrl/Cmd-drag on the photograph turns it freely."
      onclick={() => rotateBy(1)}>↻</button
    >
    <button
      type="button"
      class:armed={straightening}
      title="Straighten: click two points along the baseline (T)"
      onclick={() => (straightening ? cancelStraighten() : armStraighten())}
    >
      straighten
    </button>
    <button
      type="button"
      disabled={viewport.rotation === 0}
      title="Back to the camera's own angle (;)"
      onclick={() => resetRotation()}
    >
      reset
    </button>
  </div>

  {#if session.drawMode && !session.canDraw && drawBlockedReason}
    <!--
      Draw mode can be ON while `canDraw` is false — the toggle is a preference, the
      capability is derived. Saying "Draw mode — drag to box" in that state is a lie
      the annotator has to debug by experiment, so name the actual blocker instead.
    -->
    <div class="overlay-blocked small" role="status">{drawBlockedReason}</div>
  {/if}

  {#if hint}
    <div class="overlay-hint small">{hint}</div>
  {/if}

  {#if !session.imageUrl}
    <div class="overlay-center">
      {#if session.imageProblem}
        <div class="banner error">
          <strong>No imagery</strong>
          <p class="small">{session.imageProblem.message}</p>
          {#if session.imageProblem.code === 's3_not_configured'}
            <p class="small muted">
              Set a bucket in <a href="#/settings">Settings</a>. Imagery is never proxied through
              the service, so annotation cannot start without one.
            </p>
          {/if}
        </div>
      {:else}
        <p class="muted">Loading imagery…</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .canvas-wrap {
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    background: #0b0d11;
    border: 1px solid var(--line);
    border-radius: 8px;
    overflow: hidden;
  }

  /*
    The cursor names the gesture, including while it is only *offered*. Ctrl/Cmd-to-turn and
    Shift-to-pan used to be invisible until the button went down, which is the moment it is too
    late to be told; `data-gesture` is computed from the held modifier as well as the live drag,
    so the pointer changes as the key does. Grab is the resting state because a drag pans unless
    something says otherwise.
  */
  .stage {
    position: absolute;
    inset: 0;
    cursor: grab;
  }

  .stage[data-gesture='draw'] {
    cursor: crosshair;
  }

  .stage[data-gesture='panning'] {
    cursor: grabbing;
  }

  /*
    A circular arrow: white over a black halo, because it has to read on a sunlit limestone face
    and on a shadowed one. The fallback is `ew-resize` rather than `grabbing` — horizontal travel
    is the only axis this gesture reads, and `grabbing` would promise the photograph follows the
    pointer, which is what Shift+drag does instead.
  */
  .stage[data-gesture='rotate'],
  .stage[data-gesture='rotating'] {
    cursor:
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28'%3E%3Cpath d='M14 6A8 8 0 1 1 6.48 11.26' fill='none' stroke='%23000' stroke-width='5' stroke-linecap='round'/%3E%3Cpath d='M12.2 2.9L18.2 6 12.2 9.1Z' fill='%23000' stroke='%23000' stroke-width='3' stroke-linejoin='round'/%3E%3Cpath d='M14 6A8 8 0 1 1 6.48 11.26' fill='none' stroke='%23fff' stroke-width='2.4' stroke-linecap='round'/%3E%3Cpath d='M12.2 2.9L18.2 6 12.2 9.1Z' fill='%23fff'/%3E%3C/svg%3E")
        14 14,
      ew-resize;
  }

  .overlay-top {
    position: absolute;
    top: 8px;
    left: 10px;
    display: flex;
    gap: 12px;
    align-items: center;
    pointer-events: none;
    background: rgba(18, 21, 26, 0.82);
    border: 1px solid var(--line);
    border-radius: 5px;
    padding: 3px 8px;
  }

  .overlay-rotate {
    position: absolute;
    top: 8px;
    right: 10px;
    display: flex;
    gap: 6px;
    align-items: center;
    background: rgba(18, 21, 26, 0.82);
    border: 1px solid var(--line);
    border-radius: 5px;
    padding: 3px 6px;
  }

  .overlay-rotate button {
    background: transparent;
    border: 1px solid var(--line);
    border-radius: 4px;
    color: inherit;
    cursor: pointer;
    padding: 1px 6px;
  }

  .overlay-rotate button:disabled {
    cursor: default;
    opacity: 0.45;
  }

  .overlay-rotate button.armed {
    border-color: var(--warn);
    color: var(--warn);
  }

  /* A non-zero angle is stated in the accent colour as well as in the number, so a turned view
     is never mistaken for a crooked photograph at a glance. */
  .overlay-rotate .turned {
    color: var(--warn);
    font-weight: 600;
  }

  .overlay-hint {
    position: absolute;
    bottom: 10px;
    left: 10px;
    right: 10px;
    background: rgba(18, 21, 26, 0.92);
    border: 1px solid var(--warn);
    border-radius: 5px;
    padding: 6px 10px;
  }

  /* Sits above .overlay-hint so both can show at once without overlapping. */
  .overlay-blocked {
    position: absolute;
    bottom: 46px;
    left: 10px;
    right: 10px;
    background: rgba(18, 21, 26, 0.94);
    border: 1px solid var(--warn);
    border-left-width: 4px;
    border-radius: 5px;
    padding: 6px 10px;
  }

  .overlay-center {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    padding: 24px;
    pointer-events: auto;
  }
</style>
